/**
 * NotaRepository - Repositorio unificado para Notas
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { INotaRepository, Nota, NotaFilters } from '../interfaces/INotaRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Notas
 */
export class NotaRepository extends BaseRepository<Nota, string> implements INotaRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'notas', 'notas');

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;
  }

  async add(data: Omit<Nota, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Nota = {
      ...data,
      id,
      created_at: data.created_at || now,
      updated_at: now,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      user_id: data.user_id || profile?.user_id || '',
      is_deleted: false
    };

    await this.db.insert('notas', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<Nota> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('notas', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('notas', id);
    this.emitSync('DELETE', id);
  }

  // ==================== Soft Delete ====================
  // Estos métodos ahora vienen de BaseRepository (soporta soft delete habilitado)

  async getAllForUser(userId: string, empresaId: string): Promise<Nota[]> {
    return this.db.queryMultiple<Nota>('notas', [
      { field: 'is_deleted', operator: 'equals', value: false },
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async getByCategory(category: string, empresaId: string): Promise<Nota[]> {
    return this.db.queryMultiple<Nota>('notas', [
      { field: 'is_deleted', operator: 'equals', value: false },
      { field: 'category', operator: 'equals', value: category },
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async getDeletedByUser(userId: string): Promise<Nota[]> {
    return this.db.queryMultiple<Nota>('notas', [
      { field: 'is_deleted', operator: 'equals', value: true },
      { field: 'user_id', operator: 'equals', value: userId }
    ]);
  }

  async savePreviousContent(id: string, content: string, updatedAt: string): Promise<void> {
    await this.db.update('notas', id, {
      previous_content: content,
      previous_updated_at: updatedAt
    } as any);
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================
}
